import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Search, Download, ArrowLeft, User, Bot, Clock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConversationPanel = ({ botId }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const params = { ...(search ? { search } : {}), ...(botId ? { bot_id: botId } : {}) };
      const { data } = await axios.get(`${BACKEND_URL}/api/bot/conversations`, { params, withCredentials: true });
      setConversations(data.conversations || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [search, botId]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const openChat = async (chatId) => {
    setSelectedChat(chatId);
    setChatLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/bot/conversations/${encodeURIComponent(chatId)}`, { withCredentials: true });
      setChatMessages(data.messages || []);
    } catch {
      toast.error('Error al cargar la conversacion');
    }
    setChatLoading(false);
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/bot/conversations-export`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'conversaciones.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado');
    } catch {
      toast.error('Error al exportar');
    }
    setExporting(false);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const formatFullDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getInitial = (name) => (name || '?').charAt(0).toUpperCase();

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Cargando conversaciones...</span>
        </div>
      </div>
    );
  }

  if (conversations.length === 0 && !search) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="conversation-panel">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {selectedChat ? (
            <button onClick={() => setSelectedChat(null)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-testid="conv-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          ) : (
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-indigo-600" />
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-950">
              {selectedChat ? conversations.find(c => c.chat_id === selectedChat)?.sender_name || 'Conversacion' : 'Conversaciones'}
            </h3>
            <p className="text-xs text-gray-500">
              {selectedChat ? selectedChat.replace('@s.whatsapp.net', '') : `${conversations.length} contacto${conversations.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        {!selectedChat && (
          <Button size="sm" variant="outline" onClick={exportCSV} disabled={exporting}
            data-testid="export-csv-btn"
            className="text-xs gap-1.5 rounded-lg">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Exportar CSV
          </Button>
        )}
      </div>

      {/* List View */}
      {!selectedChat && (
        <>
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="conv-search-input"
                type="text" value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre o numero..."
                className="w-full bg-gray-100 border-0 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:bg-white transition-all placeholder:text-gray-400"
              />
            </div>
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {conversations.length === 0 && search && (
              <div className="p-6 text-center text-sm text-gray-400" data-testid="conv-no-results">
                No se encontraron resultados para "{search}"
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.chat_id}
                onClick={() => openChat(c.chat_id)}
                data-testid={`conv-item-${c.chat_id}`}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-semibold text-indigo-700">{getInitial(c.sender_name)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-950 truncate">{c.sender_name}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">{formatTime(c.last_timestamp)}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{c.last_message}</p>
                </div>
                <div className="bg-gray-100 text-gray-600 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0">
                  {c.message_count}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Chat Detail View */}
      {selectedChat && (
        <div className="max-h-[450px] overflow-y-auto px-4 py-4 space-y-3 bg-gray-50" data-testid="conv-detail">
          {chatLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
            </div>
          ) : chatMessages.map((msg, i) => (
            <div key={i} className="space-y-2">
              {/* Incoming */}
              <div className="flex items-start gap-2" data-testid={`conv-msg-in-${i}`}>
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-md px-3 py-2 text-sm text-gray-800 max-w-[280px]">
                    {msg.incoming_message}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5 ml-1">
                    <Clock className="w-2.5 h-2.5 text-gray-300" />
                    <span className="text-[10px] text-gray-400">{formatFullDate(msg.timestamp)}</span>
                  </div>
                </div>
              </div>
              {/* Bot Response */}
              <div className="flex items-start gap-2 justify-end" data-testid={`conv-msg-out-${i}`}>
                <div>
                  <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-md px-3 py-2 text-sm max-w-[280px]">
                    {msg.bot_response}
                  </div>
                </div>
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-3.5 h-3.5 text-indigo-600" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConversationPanel;
